'use client'

import { Box, Flex, Grid, Text, Heading } from '@chakra-ui/react'
import { DoubleColor } from '@/__generated__/graphql'
import JackpotIcon from '@/public/assets/images/double/double-jakcpot-icon-xl.png'
import Image from 'next/image'

import { simulate_gen_range } from '../pkg/rng_v1_web'
import { useEffect, useState } from 'react'
import { sha256 } from '@noble/hashes/sha256'
import { DoubleField } from '../../Double/atoms/DoubleField'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'
import { useTranslation } from 'next-i18next/pages'

const schema = yup
  .object({
    signature: yup
      .string()
      .required()
      .label('Signature')
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
  })
  .required()

export const DoubleVerify = () => {
  const { t } = useTranslation()
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const [state, setState] = useState<{ winNumber: number; jackpotNumber: number } | undefined>()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      setState(undefined)
      return
    }

    const signature = form.getValues('signature').replace('0x', '')

    setState({
      winNumber: parseInt(
        simulate_gen_range(
          Buffer.from(sha256(Buffer.from(signature, 'hex'))).toString('hex'),
          Buffer.from('double').toString('hex'),
          1,
          '0',
          '15',
          false
        )
      ),
      jackpotNumber: parseInt(
        simulate_gen_range(
          Buffer.from(sha256(Buffer.from(signature, 'hex'))).toString('hex'),
          Buffer.from('double-jackpot').toString('hex'),
          1,
          '0',
          '4001',
          false
        )
      ),
    })
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <Box p={5} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <VerifyFormTitle mode="drand" />
          <VerifyFormTextarea
            label={t('verify.signature')}
            placeholder={t('verify.enter-signature')}
            name="signature"
          />
          <VerifyFormButton onClick={handleClick} />
        </FormProvider>
      </Box>
      <Box p={5} minH={{ base: '0', lg: '375px' }} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'} mb={{ base: 2, lg: 0 }}>
          {t('common.result')}
        </Heading>
        {!state ? (
          <EmptyPlaceholder game={Section.DOUBLE} />
        ) : (
          <DoubleVerifyResult winNumber={state.winNumber} jackpotNumber={state.jackpotNumber} />
        )}
      </Box>
    </Grid>
  )
}

const DoubleVerifyResult = ({ winNumber, jackpotNumber }: { winNumber: number; jackpotNumber: number }) => {
  const { t } = useTranslation()
  return (
    <Flex
      gap={6}
      flexDir={'column'}
      justifyContent={'center'}
      h={{ base: 'auto', lg: 'calc(100% - 24px)' }}
      alignItems={'center'}
    >
      <Flex gap={3} rounded={'xl'} border={'1px solid'} borderColor={'ninja.500'} p={3} alignItems={'center'}>
        <Flex alignItems={'center'} justifyContent={'center'} boxSize={12}>
          <Box mt={2} flexShrink={0} boxSize={24}>
            <Image src={JackpotIcon} alt={''} />
          </Box>
        </Flex>
        <Box fontWeight={700}>
          <Text fontSize={'xs'}>{t('common.jackpot-number')}</Text>
          <Text color={jackpotNumber != 0 ? 'white' : 'brand.green'}>
            {jackpotNumber}{' '}
            <Text as={'span'} color={'ninja.400'}>
              (0-4000)
            </Text>
          </Text>
        </Box>
      </Flex>
      <Flex justifyContent={'center'} flexWrap={'wrap'} gap={1.5}>
        <Box opacity={winNumber != 0 ? 0.4 : 1}>
          <DoubleField boxSize={10} type={DoubleColor.Green} />
          <Text fontWeight={700} mt={2} fontSize={'sm'} textAlign={'center'}>
            0
          </Text>
        </Box>
        {Array.from({ length: 15 }, (_, index) => (
          <Box opacity={index + 1 != winNumber ? 0.4 : 1} key={index}>
            <DoubleField boxSize={10} type={index % 2 === 0 ? DoubleColor.Red : DoubleColor.Black} />
            <Text fontWeight={700} mt={2} fontSize={'sm'} textAlign={'center'}>
              {index + 1}
            </Text>
          </Box>
        ))}
      </Flex>
    </Flex>
  )
}
